import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./JobDetail.module.scss";
import classNames from "classnames/bind";
import {
  EstimateTabIcon,
  FinancialTabIcon,
  JobTaskTabIcon,
  MaterialTabIcon,
  OverviewTabIcon,
  PhotoTabIcon,
  WorkOrderTabIcon,
  DocumentNewIcon,
  MailIcon,
  PhoneIcon,
  CloseV2Icon,
  WarningIcon,
  CloseIcon,
  CalendarIcon,
} from "~/assets/icons";
import OverviewTab from "./tabs/overview/OverviewTab";
import JobTaskTab from "./tabs/job-task/JobTaskTab";
import DocumentTab from "./tabs/document/DocumentTab";
import PhotoTab from "./tabs/photo/PhotoTab";
import FinancialTab, { FinancialTabRef } from "./tabs/financial/FinancialTab";
import EstimateTab from "./tabs/estimate/EstimateTab";
import MaterialOrderTab from "./tabs/material-order/MaterialOrderTab";
import WorkOrderTab from "./tabs/work-order/WorkOrderTab";
import MailboxTab from "./tabs/mailbox/MailboxTab";
import CalendarTab from "./tabs/calendar/CalendarTab";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon, PlusIcon } from "lucide-react";
import NoteTab from "./tabs/note/NoteTab";
import MessageTab from "./tabs/message/MessageTab";
import { adminRouteAbsolute } from "~/utils/constants/route";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { notify } from "~/utils/helper";
import {
  handleGetJobDetails,
  handleJobDetailsAddContact,
  handleUpdateOverviewCustomerDetails,
} from "~/thunks/job-details/jobDetailsThunk";
import { jobDetailsActions } from "~/thunks/job-details/jobDetailsSlice";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import UploadPhoto from "./tabs/photo/UploadPhoto";
import { handleMoveStage, handleUploadPhotos, handleUpdateJob } from "~/thunks/job/jobThunk";
import jobDetailsApi from "~/apis/jobDetails";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { JobStageOptions } from "../add-job/constants/add-job.constants";
import EditCustomerModal from "./tabs/overview/component/edit-customer-details/EditCustomerModal";
import calendarImage from "~/assets/images/calendar.svg";
import BaseModal from "~/components/common/base-modal/BaseModal";
import AddAppointmentModal from "~/components/schedules/add-appointment-modal/AddAppointmentModal";
import { scheduleActions } from "~/thunks/schedule/scheduleSlice";
import userManagementApi from "~/apis/userManagement";
import type { IEmployee } from "~/apis/userManagement";
import { ICustomer, IPrimaryContact, ISecondaryContact } from "~/utils/interface/jobDetails";

const cx = classNames.bind(styles);

const getEmployeeDisplayName = (emp: IEmployee) => {
  const first = emp.profile?.firstName ?? "";
  const last = emp.profile?.lastName ?? "";
  const name = `${first} ${last}`.trim();
  return name || emp.email || "Unknown";
};

const MENTION_TAG = "span";

function getPlainTextAndMentions(container: HTMLElement | null): { text: string; ids: number[] } {
  if (!container) return { text: "", ids: [] };
  let text = "";
  const ids: number[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? "";
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === "BR") {
      text += "\n";
      return;
    }
    if (el.hasAttribute("data-mention-id")) {
      const id = Number(el.getAttribute("data-mention-id"));
      if (!Number.isNaN(id)) ids.push(id);
      text += el.textContent ?? "";
      return;
    }
    el.childNodes.forEach(walk);
  };
  container.childNodes.forEach(walk);
  return { text, ids };
}

/** User-typed text only (excludes mention span content). Use for @ detection so @ in emails doesn't trigger. */
function getUserTypedTextToSelection(container: HTMLElement | null, sel: Selection | null): string {
  if (!container || !sel || sel.rangeCount === 0) return "";
  const range = sel.getRangeAt(0);
  const pre = range.cloneRange();
  pre.selectNodeContents(container);
  pre.setEnd(range.startContainer, range.startOffset);
  const frag = pre.cloneContents();
  const div = document.createElement("div");
  div.appendChild(frag);
  let s = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      s += node.textContent ?? "";
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === "BR") {
      s += "\n";
      return;
    }
    if (el.hasAttribute("data-mention-id")) return;
    el.childNodes.forEach(walk);
  };
  div.childNodes.forEach(walk);
  return s;
}

function getNodeAndOffsetForUserTypedIndex(
  container: HTMLElement,
  sel: Selection | null,
  targetIndex: number
): { node: Node; offset: number } | null {
  if (!sel || sel.rangeCount === 0) return null;
  const anchor = sel.anchorNode;
  const anchorOffset = sel.anchorOffset;
  if (!anchor) return null;

  const limit = document.createRange();
  limit.selectNodeContents(container);
  limit.setEnd(anchor, anchorOffset);

  let count = 0;
  let result: { node: Node; offset: number } | null = null;

  const beforeAnchor = (node: Node, offset: number) => {
    const r = document.createRange();
    r.setStart(container, 0);
    r.setEnd(node, offset);
    return limit.compareBoundaryPoints(Range.END_TO_END, r) > 0;
  };

  const walk = (node: Node, insideMention: boolean): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (insideMention) return false;
      const text = node.textContent ?? "";
      for (let i = 0; i < text.length; i++) {
        if (!beforeAnchor(node, i)) return true;
        if (count === targetIndex) {
          result = { node, offset: i };
          return true;
        }
        count++;
      }
      return false;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const el = node as HTMLElement;
    if (el.tagName === "BR") {
      if (insideMention) return false;
      if (!beforeAnchor(el.parentNode ?? el, Array.from((el.parentNode ?? el).childNodes).indexOf(el))) return true;
      if (count === targetIndex) {
        const p = el.parentNode ?? el;
        result = { node: p, offset: Array.from(p.childNodes).indexOf(el) };
        return true;
      }
      count++;
      return false;
    }
    const isMention = el.hasAttribute("data-mention-id");
    for (let i = 0; i < el.childNodes.length; i++) {
      if (walk(el.childNodes[i], insideMention || isMention)) return true;
    }
    return false;
  };

  for (let i = 0; i < container.childNodes.length; i++) {
    if (walk(container.childNodes[i], false)) break;
  }
  return result;
}

const tabs = [
  {
    key: "overview",
    label: "Overview",
    icon: <OverviewTabIcon className="w-[22px]" />,
  },
  {
    key: "photos",
    label: "Photos",
    icon: <PhotoTabIcon className="w-[16px]" />,
  },
  {
    key: "documents",
    label: "Documents",
    icon: <DocumentNewIcon className="w-[16px]" />,
  },
  {
    key: "estimates",
    label: "Estimates",
    icon: <EstimateTabIcon className="w-[16px]" />,
  },
  {
    key: "material-order",
    label: "Materials",
    icon: <MaterialTabIcon className="w-[16px]" />,
  },
  {
    key: "work-order",
    label: "Work Order",
    icon: <WorkOrderTabIcon className="w-[16px]" />,
  },
  {
    key: "financials",
    label: "Financials",
    icon: <FinancialTabIcon className="w-[20px]" />,
  },
  {
    key: "job-tasks",
    label: "Job Tasks",
    icon: <JobTaskTabIcon className="w-[16px]" />,
  },
  {
    key: "calendar",
    label: "Calendar",
    icon: <CalendarIcon className="w-[16px]" />,
  },
  {
    key: "mailbox",
    label: "Mailbox",
    icon: <MailIcon className="w-[16px]" />,
  },
];

const tabs2 = [
  {
    key: "note",
    label: "Notes",
  },
  {
    key: "message",
    label: "Message",
  },
];

const JobDetail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const jobId = id || window.location.pathname.split("/").pop() || "";
  const {
    jobDetails,
    isLoadingJobDetails,
    timeUpdatePhoto,
    isUpdatingJobDetails,
    errorJobDetails,
  } = useAppSelector((state) => state.jobDetails);
  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [activeTab2, setActiveTab2] = useState(tabs2[0].key);
  const [tabTransition, setTabTransition] = useState(false);
  const [tabTransition2, setTabTransition2] = useState(false);
  const [isOpendUpload, setOpendUpload] = useState(false);
  const [isOpendAddNoteModal, setIsOpendAddNoteModal] = useState(false);
  const [isOpendAddContactModal, setIsOpendAddContactModal] = useState(false);
  const [isOpenEditContactModal, setIsOpenEditContactModal] = useState(false);
  const [editContactDetail, setEditContactDetail] = useState<ICustomer | null>(
    null
  );
  const [dataAddNote, setDataAddNote] = useState({
    title: "",
    content: "",
    private: false,
    mentionedAccountIds: [] as number[],
  });
  const [errorsAddNote, setErrorsAddNote] = useState({
    title: "",
    content: "",
  });
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState("");
  const [mentionDropdownIndex, setMentionDropdownIndex] = useState(0);
  const [mentionPosition, setMentionPosition] = useState<{ top: number; left: number } | null>(null);
  const [mentionAtIndex, setMentionAtIndex] = useState<number | null>(null);
  const noteContentRef = useRef<HTMLDivElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  const mentionListRef = useRef<HTMLDivElement>(null);
  const financialTabRef = useRef<FinancialTabRef>(null);
  const backFromWorkOrderDetailsRef = useRef<(() => void) | null>(null);
  const backFromFinancialDetailsRef = useRef<(() => void) | null>(null);
  const backFromMaterialOrderDetailsRef = useRef<(() => void) | null>(null);
  const mentionMeasureRef = useRef<HTMLDivElement>(null);
  const mentionSearchRequestIdRef = useRef(0);
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [isCompletedAppointmentModal, setIsCompletedAppointmentModal] =
    useState(false);
  const [openScheduleAppointment, setOpenScheduleAppointment] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [isOpendModalPreview, setOpendModalPreview] =
    useState(false);
  const [isWorkOrderDetailsOpen, setIsWorkOrderDetailsOpen] = useState(false);
  const [isFinancialDetailsOpen, setIsFinancialDetailsOpen] = useState(false);
  const [isMaterialOrderDetailsOpen, setIsMaterialOrderDetailsOpen] = useState(false);

  useEffect(() => {
    if (activeTab !== "work-order") {
      setIsWorkOrderDetailsOpen(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "financials") {
      setIsFinancialDetailsOpen(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "material-order") {
      setIsMaterialOrderDetailsOpen(false);
    }
  }, [activeTab]);

  const [employee, setEmployee] = useState<IEmployee[] | null>(null);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  useEffect(() => {
    if (isOpendAddNoteModal) {
      const fetchEmployee = async () => {
        setIsLoadingEmployees(true);
        try {
          const response = await userManagementApi.getEmployees({});
          if (response.data?.data?.responses) {
            setEmployee(response.data.data.responses);
          }
        } catch (error) {
          console.error("Failed to fetch employees:", error);
        } finally {
          setIsLoadingEmployees(false);
        }
      };
      fetchEmployee();
    }
  }, [isOpendAddNoteModal]);

  // Fetch employees with textSearch when @ mention search query changes (debounced).
  // When user deletes back to empty query, we refetch all (no textSearch) so dropdown matches.
  // Request ID ensures we ignore stale responses (e.g. "john" overwriting "jo" when deleting).
  useEffect(() => {
    if (!showMentionDropdown || !isOpendAddNoteModal) return;

    const timeoutId = setTimeout(async () => {
      const requestId = ++mentionSearchRequestIdRef.current;
      const query = mentionSearchQuery.trim();
      setIsLoadingEmployees(true);
      try {
        const params: { textSearch?: string } = {};
        if (query) params.textSearch = query;
        const response = await userManagementApi.getEmployees(params);
        if (requestId !== mentionSearchRequestIdRef.current) return;
        if (response.data?.data?.responses) {
          setEmployee(response.data.data.responses);
        }
      } catch (error) {
        if (requestId !== mentionSearchRequestIdRef.current) return;
        console.error("Failed to search employees:", error);
      } finally {
        if (requestId === mentionSearchRequestIdRef.current) {
          setIsLoadingEmployees(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [mentionSearchQuery, showMentionDropdown, isOpendAddNoteModal]);

  useEffect(() => {
    if (isOpendAddNoteModal) {
      setDataAddNote({
        title: "",
        content: "",
        private: false,
        mentionedAccountIds: [],
      });
      setErrorsAddNote({ title: "", content: "" });
      setShowMentionDropdown(false);
      setMentionSearchQuery("");
      setMentionDropdownIndex(0);
      setMentionPosition(null);
      setMentionAtIndex(null);
      const el = noteContentRef.current;
      if (el) el.innerHTML = "";
    }
  }, [isOpendAddNoteModal]);

  const calculateMentionPosition = useCallback((plainTextBeforeAt: string) => {
    const editable = noteContentRef.current;
    const measureDiv = mentionMeasureRef.current;
    if (!editable || !measureDiv) return null;

    const editableRect = editable.getBoundingClientRect();
    const lines = plainTextBeforeAt.split("\n");
    const lastLine = lines[lines.length - 1];

    const styles = window.getComputedStyle(editable);
    measureDiv.style.font = styles.font;
    measureDiv.style.fontSize = styles.fontSize;
    measureDiv.style.fontFamily = styles.fontFamily;
    measureDiv.style.fontWeight = styles.fontWeight;
    measureDiv.style.fontStyle = styles.fontStyle;
    measureDiv.style.letterSpacing = styles.letterSpacing;
    measureDiv.style.padding = styles.padding;
    measureDiv.style.border = styles.border;
    measureDiv.style.boxSizing = styles.boxSizing;
    measureDiv.style.whiteSpace = "pre-wrap";
    measureDiv.style.wordWrap = "break-word";
    measureDiv.style.width = `${editable.offsetWidth}px`;
    measureDiv.style.visibility = "hidden";
    measureDiv.style.position = "absolute";
    measureDiv.style.top = "0";
    measureDiv.style.left = "0";
    measureDiv.style.zIndex = "-1";

    measureDiv.textContent = plainTextBeforeAt;

    void measureDiv.offsetHeight;

    const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.2;
    const paddingTop = parseFloat(styles.paddingTop) || 0;
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const borderTop = parseFloat(styles.borderTopWidth) || 0;
    const borderLeft = parseFloat(styles.borderLeftWidth) || 0;

    const lastLineIndex = lines.length - 1;

    const tempSpan = document.createElement("span");
    tempSpan.style.font = styles.font;
    tempSpan.style.fontSize = styles.fontSize;
    tempSpan.style.fontFamily = styles.fontFamily;
    tempSpan.style.fontWeight = styles.fontWeight;
    tempSpan.style.letterSpacing = styles.letterSpacing;
    tempSpan.style.visibility = "hidden";
    tempSpan.style.position = "absolute";
    tempSpan.style.whiteSpace = "pre";
    tempSpan.textContent = lastLine;
    document.body.appendChild(tempSpan);
    const lastLineWidth = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);

    const scrollTop = (editable as HTMLElement).scrollTop;

    const top = editableRect.top + paddingTop + borderTop + (lastLineIndex * lineHeight) - scrollTop + lineHeight;
    const left = editableRect.left + paddingLeft + borderLeft + lastLineWidth;

    return { top, left };
  }, []);

  useEffect(() => {
    if (!showMentionDropdown) return;

    const closeDropdown = () => {
      setShowMentionDropdown(false);
      setMentionPosition(null);
      setMentionAtIndex(null);
      setMentionSearchQuery("");
      setMentionDropdownIndex(0);
    };

    const handleScroll = (e: Event) => {
      const target = e.target as Node;
      const listEl = mentionListRef.current;
      if (listEl && (target === listEl || listEl.contains(target))) {
        return;
      }
      closeDropdown();
    };

    const editable = noteContentRef.current;
    if (editable) {
      editable.addEventListener("scroll", handleScroll);
    }
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      if (editable) {
        editable.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showMentionDropdown]);

  const filteredEmployees = employee ?? [];

  const handleMentionSelect = useCallback(
    (emp: IEmployee) => {
      const editable = noteContentRef.current;
      const sel = window.getSelection();
      if (!editable || !sel || sel.rangeCount === 0) return;

      const userTypedToCursor = getUserTypedTextToSelection(editable, sel);
      const lastAt = userTypedToCursor.lastIndexOf("@");
      if (lastAt === -1) return;

      const startInfo = getNodeAndOffsetForUserTypedIndex(editable, sel, lastAt);
      if (!startInfo) return;

      const range = sel.getRangeAt(0);
      const r = document.createRange();
      r.setStart(startInfo.node, startInfo.offset);
      r.setEnd(range.startContainer, range.startOffset);

      r.deleteContents();

      const span = document.createElement(MENTION_TAG);
      span.setAttribute("data-mention-id", String(emp.id));
      span.setAttribute("contenteditable", "false");
      span.className = cx("mention");
      span.textContent = getEmployeeDisplayName(emp);

      r.insertNode(span);
      const space = document.createTextNode("\u00A0");
      r.setStartAfter(span);
      r.setEndAfter(span);
      r.insertNode(space);
      r.setStartAfter(space);
      r.setEndAfter(space);
      sel.removeAllRanges();
      sel.addRange(r);

      setShowMentionDropdown(false);
      setMentionSearchQuery("");
      setMentionDropdownIndex(0);
      setMentionPosition(null);
      setMentionAtIndex(null);
      editable.focus();
    },
    []
  );

  const handleNoteContentInput = useCallback(
    () => {
      const editable = noteContentRef.current;
      const sel = window.getSelection();
      const userTypedToCursor = getUserTypedTextToSelection(editable, sel);
      const cursorOffset = userTypedToCursor.length;

      const lastAt = userTypedToCursor.lastIndexOf("@", cursorOffset - 1);
      const between = userTypedToCursor.slice(lastAt + 1, cursorOffset);
      const spaceAfter = between.includes(" ");
      if (lastAt === -1 || spaceAfter) {
        setShowMentionDropdown(false);
        setMentionPosition(null);
        setMentionAtIndex(null);
        return;
      }
      const query = between;
      setMentionSearchQuery(query);
      setMentionAtIndex(lastAt);

      const position = calculateMentionPosition(userTypedToCursor.slice(0, lastAt));
      setMentionPosition(position);
      setShowMentionDropdown(true);
      setMentionDropdownIndex(0);
    },
    [calculateMentionPosition]
  );

  const handleNoteContentPaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }, []);

  const handleNoteContentKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!showMentionDropdown || filteredEmployees.length === 0) return;
      if (e.key === "Escape") {
        setShowMentionDropdown(false);
        setMentionPosition(null);
        setMentionAtIndex(null);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const emp = filteredEmployees[mentionDropdownIndex];
        if (emp) handleMentionSelect(emp);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionDropdownIndex((i) =>
          i < filteredEmployees.length - 1 ? i + 1 : 0
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionDropdownIndex((i) =>
          i > 0 ? i - 1 : filteredEmployees.length - 1
        );
      }
    },
    [
      showMentionDropdown,
      filteredEmployees,
      mentionDropdownIndex,
      handleMentionSelect,
    ]
  );

  useEffect(() => {
    dispatch(jobDetailsActions.resetJobDetailsState());
  }, [dispatch, jobId]);

  useEffect(() => {
    if (!jobId) return;

    dispatch(handleGetJobDetails(jobId))
      .unwrap()
      .catch((error: { message?: string } | string) => {
        const message =
          typeof error === "string"
            ? error
            : error?.message || "Failed to fetch job details";
        notify("error", message);
      });
  }, [dispatch, jobId, timeUpdatePhoto]);

  useEffect(() => {
    return () => {
      dispatch(jobDetailsActions.resetJobDetailsState());
    };
  }, [dispatch]);

  useEffect(() => {
    const keys: any = [];
    searchParams.forEach((value, key) => {
      keys.push(key);
    });

    if (searchParams.has("view-details") || searchParams.has("budget")) {
      const newSearchParams = new URLSearchParams(searchParams);
      tabs.forEach((tab) => {
        if (tab.key !== "financials") {
          newSearchParams.delete(tab.key);
        }
      });
      if (!newSearchParams.has("financials")) {
        newSearchParams.set("financials", "");
      }
      const newQuery = newSearchParams.toString();
      const currentQuery = searchParams.toString();
      if (newQuery !== currentQuery) {
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${newQuery}`
        );
      }

      if (activeTab !== "financials") {
        setActiveTab("financials");
      }
      return;
    }

    for (let i = 0; i < tabs.length; i++) {
      let tabKey = tabs[i].key;
      if (keys.includes(tabKey)) {
        searchParams.delete(activeTab);
        searchParams.delete(tabKey);
        searchParams.delete("view-details");
        const newQuery =
          tabKey +
          (searchParams.toString() ? "&" + searchParams.toString() : "");
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${newQuery}`
        );

        setActiveTab(tabKey);
        return;
      }
    }
  }, [activeTab, searchParams]);

  const performTabChange = useCallback(
    (key: string) => {
      searchParams.delete(activeTab);
      searchParams.delete("view-details");
      const newQuery =
        key + (searchParams.toString() ? "&" + searchParams.toString() : "");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${newQuery ? `?${newQuery}` : ""}`
      );
      setActiveTab(key);
      setTabTransition(false);
    },
    [activeTab, searchParams]
  );

  const handleTabChange = (key: string) => {
    if (key !== activeTab) {
      if (activeTab === "financials") {
        financialTabRef.current?.requestLeaveFromCreateBudget(() => {
          setTabTransition(true);
          setTimeout(() => performTabChange(key), 200);
        });
      } else {
        setTabTransition(true);
        setTimeout(() => performTabChange(key), 200);
      }
    }
  };

  const handleTabChange2 = (key: string) => {
    if (key !== activeTab2) {
      setTabTransition2(true);
      setTimeout(() => {
        setActiveTab2(key);
        setTabTransition2(false);
      }, 200);
    }
  };

  const handleAddNote = () => {
    setIsOpendAddNoteModal(true);
  };

  const validateAddNote = () => {
    let newErrors = { title: "", content: "" };
    const { text } = getPlainTextAndMentions(noteContentRef.current);

    if (!dataAddNote.title.trim()) newErrors.title = "Title is required";
    if (!text.trim()) newErrors.content = "Content is required";

    setErrorsAddNote(newErrors);
    return !newErrors.title && !newErrors.content;
  };

  const handleAddNoteSubmit = async () => {
    if (!id) return;
    if (!validateAddNote()) return;

    const { text, ids } = getPlainTextAndMentions(noteContentRef.current);
    const payload = {
      title: dataAddNote.title,
      content: text,
      isPrivate: dataAddNote.private,
      mentionedAccountIds: ids,
    };
    try {
      await jobDetailsApi.addNote(id, payload);
      setDataAddNote({ title: "", content: "", private: false, mentionedAccountIds: [] });
      setErrorsAddNote({ title: "", content: "" });
      setShowMentionDropdown(false);
      setMentionSearchQuery("");
      setMentionDropdownIndex(0);
      if (noteContentRef.current) noteContentRef.current.innerHTML = "";
      notify("success", "Note added successfully!");
      setIsOpendAddNoteModal(false);
      await dispatch(handleGetJobDetails(jobId));
    } catch (error: any) {
      notify("error", `Failed to add note: ${error?.response?.data?.message}`);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return <OverviewTab jobId={jobId} />;
      case "job-tasks":
        return <JobTaskTab jobId={jobId} />;
      case "documents":
        return <DocumentTab />;
      case "photos":
        return <PhotoTab />;
      case "financials":
        return (
          <FinancialTab
            ref={financialTabRef}
            key={`financials-${searchParams.get("subtab") ?? ""}`}
            jobId={jobId}
            onAddNote={handleAddNote}
            initialSubTab={
              searchParams.get("subtab") === "budget-tracker"
                ? "budget-tracker"
                : undefined
            }
            onDetailsOpenChange={setIsFinancialDetailsOpen}
            onRegisterBackFromDetails={(fn) => {
              backFromFinancialDetailsRef.current = fn ?? null;
            }}
          />
        );
      case "estimates":
        return <EstimateTab />;
      case "material-order":
        return (
          <MaterialOrderTab
            jobId={jobId}
            onDetailsOpenChange={setIsMaterialOrderDetailsOpen}
            onRegisterBackFromDetails={(fn) => {
              backFromMaterialOrderDetailsRef.current = fn ?? null;
            }}
          />
        );
      case "work-order":
        return (
          <WorkOrderTab
            jobId={jobId}
            onDetailsOpenChange={setIsWorkOrderDetailsOpen}
            onRegisterBackFromDetails={(fn) => {
              backFromWorkOrderDetailsRef.current = fn ?? null;
            }}
          />
        );
      case "calendar":
        return <CalendarTab jobId={jobId} />;
      case "mailbox":
        return <MailboxTab />;
      default:
        return null;
    }
  };

  const renderTabContent2 = () => {
    switch (activeTab2) {
      case "note":
        return (
          <NoteTab notes={jobDetails?.notes || []} onAddNote={handleAddNote} />
        );
      case "message":
        return (
          <MessageTab
            jobId={jobId}
            primaryPhone={jobDetails?.relatedContacts?.primaryContact?.phoneNumber}
            secondaryPhone={jobDetails?.relatedContacts?.secondaryContact?.phoneNumber}
          />
        );
      default:
        return null;
    }
  };

  const handleButtonBackClick = () => {
    if (activeTab === "financials") {
      financialTabRef.current?.requestLeaveFromCreateBudget(() => {
        navigate(adminRouteAbsolute.job.main);
      });
    } else {
      navigate(adminRouteAbsolute.job.main);
    }
  };

  const handleUpload = async (data: any) => {
    if (!id) return;
    if (isUpdatingJobDetails) return;

    const files: File[] =
      Array.isArray(data.files) && data.files.length > 0
        ? data.files
        : data.file
          ? [data.file]
          : [];

    if (files.length === 0) {
      notify("error", "Please select at least one file.");
      return;
    }

    try {
      for (const file of files) {
        await dispatch(
          handleUploadPhotos({
            id: id,
            param: {
              file,
              description: data.description,
              title: data.title,
            },
          })
        ).unwrap();
      }
      notify(
        "success",
        files.length > 1
          ? `${files.length} photos uploaded successfully!`
          : "Upload successful!"
      );
      setOpendUpload(false);
    } catch (error) {
      notify("error", "Upload failed!");
    }
  };

  const getPriorityClass = (priority: string | undefined): string => {
    if (!priority) return "";
    const upperPriority = priority.toUpperCase();
    if (upperPriority === "HIGH") return "High";
    if (upperPriority === "MEDIUM") return "Medium";
    if (upperPriority === "LOW") return "Low";
    return "";
  };

  const getPriorityText = (priority: string | undefined): string => {
    if (!priority) return "";
    return priority.toLowerCase();
  };

  const handleStageChange = async (newStageValue: string) => {
    if (!jobId || !jobDetails) return;

    if (newStageValue === jobDetails.jobStage) {
      return;
    }

    const newStage = JobStageOptions.find(
      (option) => option.value === newStageValue
    );

    if (!newStage) {
      notify("error", "Invalid stage selected");
      return;
    }

    try {
      setIsUpdatingStage(true);

      await dispatch(
        handleMoveStage({
          id: jobId,
          params: { jobStage: newStageValue },
        })
      ).unwrap();

      notify("success", "Job stage updated successfully!");

      // Refresh job details to get latest data
      await dispatch(handleGetJobDetails(jobId)).unwrap();
    } catch (error: any) {
      console.error("Failed to update job stage:", error);
      notify(
        "error",
        `Failed to update job stage: ${error?.response?.data?.message || "Unknown error"
        }`
      );
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleAddContact = () => {
    setIsOpendAddContactModal(true);
  };

  const mapRelatedContactToCustomer = (
    contact?: IPrimaryContact | ISecondaryContact | null
  ): ICustomer | null => {
    if (!contact?.id) return null;
    return {
      id: String(contact.id),
      name: contact.name,
      primaryEmail: contact.email,
      primaryPhone: contact.phoneNumber,
      addressLine1: contact.address,
      city: contact.city,
      state: contact.stateProvince,
      postalCode: contact.zipCodePostalCode,
      country: contact.country,
      referredBy: contact.referredBy,
    };
  };

  const handleOpenRelatedContact = (
    contact?: IPrimaryContact | ISecondaryContact | null
  ) => {
    const customer = mapRelatedContactToCustomer(contact);
    if (!customer) return;
    setEditContactDetail(customer);
    setIsOpenEditContactModal(true);
  };

  const handleSaveEditContact = async (data: any) => {
    if (!editContactDetail?.id) return;
    try {
      await dispatch(
        handleUpdateOverviewCustomerDetails({
          id: editContactDetail.id,
          data,
        })
      ).unwrap();
      notify("success", "Customer updated successfully!");
      dispatch(handleGetJobDetails(jobId));
      setIsOpenEditContactModal(false);
      setEditContactDetail(null);
    } catch (error: any) {
      notify(
        "error",
        `Failed to update customer: ${
          error?.response?.data?.message || error?.message || "Unknown error"
        }`
      );
    }
  };

  const handleOpenCompletedAppointmentModal = () => {
    setIsCompletedAppointmentModal(true);
  };

  const handleCloseCompletedAppointmentModal = () => {
    setIsCompletedAppointmentModal(false);
  };

  const handleMarkAppointmentAsCompleted = async () => {
    if (!jobId) return;

    try {
      await dispatch(
        handleUpdateJob({
          id: jobId,
          data: { needAppointment: true },
        })
      ).unwrap();

      notify("success", "Appointment marked as completed!");
      setIsCompletedAppointmentModal(false);

      // Refresh job details to get latest data
      await dispatch(handleGetJobDetails(jobId)).unwrap();
    } catch (error: any) {
      console.error("Failed to update appointment:", error);
      notify(
        "error",
        `Failed to update appointment: ${error?.response?.data?.message || "Unknown error"
        }`
      );
    }
  };

  return (
    <div className={cx("job-detail-container")}>
      {(isLoadingJobDetails || isUpdatingJobDetails) && !errorJobDetails && (
        <div className={cx("loading-overlay")}>
          <LoadingMainContent />
        </div>
      )}
      <div className={cx("header")}>
        <OutlinedButton
          title={isWorkOrderDetailsOpen || isFinancialDetailsOpen || isMaterialOrderDetailsOpen ? "Back" : "Back to Job Boards"}
          icon={<ArrowLeftIcon className={cx("back-icon")} />}
          onClick={
            isWorkOrderDetailsOpen
              ? () => backFromWorkOrderDetailsRef.current?.()
              : isFinancialDetailsOpen
                ? () => backFromFinancialDetailsRef.current?.()
                : isMaterialOrderDetailsOpen
                  ? () => backFromMaterialOrderDetailsRef.current?.()
                  : handleButtonBackClick
          }
          className={cx("back-button")}
        />
      </div>

      {!isLoadingJobDetails && errorJobDetails && (
        <div className={cx("error-container")}>
          <div className={cx("error-content")}>
            <WarningIcon className={cx("error-icon")} />
            <h2 className={cx("error-title")}>Job doesn't exist</h2>
            <p className={cx("error-message")}>
              The job you're looking for doesn't exist or has been removed.
            </p>
            <PrimaryButton
              title="Back to Job Boards"
              onClick={handleButtonBackClick}
              className={cx("back-to-jobs-button")}
            />
          </div>
        </div>
      )}

      {!errorJobDetails && jobDetails && (
        <div className={cx("content", { "content-full-width": isWorkOrderDetailsOpen || isFinancialDetailsOpen || isMaterialOrderDetailsOpen })}>
          <div className={cx("left-content")}>
            <div className={cx("left-content-header")}>
              <div className={cx("info")}>
                <div className={cx("title-container")}>
                  <h2 className={cx("title")}>{jobDetails?.name}</h2>

                  <div className={cx("status-container")}>
                    <PrimarySelectInput
                      name="status"
                      placeholder="Select status contact"
                      value={
                        JobStageOptions.find(
                          (option) => option.value === jobDetails?.jobStage
                        )?.label || "Do Not Contact"
                      }
                      onChange={(value) => handleStageChange(value)}
                      options={JobStageOptions}
                      className={cx("status-select")}
                    />
                  </div>
                </div>
                <div className={cx("description-container")}>
                  <p className={cx("description", "description-job-id")}>Job ID: {jobDetails?.jobId}</p>
                  <div className={cx("separator")}></div>
                  <p className={cx("description", "description-address")}>
                    Address: {jobDetails?.address}
                  </p>

                  {jobDetails?.priority && (
                    <p className={cx(getPriorityClass(jobDetails?.priority))}>
                      {getPriorityText(jobDetails?.priority)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className={cx("left-content-body")}>
              <div className={cx("left-content-body-menu")}>
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={cx("left-content-body-menu-item", {
                      active: activeTab === tab.key,
                    })}
                    onClick={() => handleTabChange(tab.key)}
                  >
                    {tab.icon}
                    <p className={cx("left-content-body-menu-item-label")}>
                      {tab.label}
                    </p>
                  </button>
                ))}
              </div>
              <div
                className={cx("left-content-body-content", {
                  "fade-in": !tabTransition,
                  "fade-out": tabTransition,
                })}
              >
                {renderTabContent()}
              </div>
            </div>
          </div>

          {activeTab !== "calendar" && (
            <div className={cx("right-content")}>
              <div className={cx("related-contacts")}>
              <div className={cx("related-contacts-header")}>
                <h4 className={cx("related-contacts-title")}>Related Contacts</h4>
              </div>
              <div className={cx("related-contacts-body")}>
                <div
                  className={cx("related-contacts-item")}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    handleOpenRelatedContact(
                      jobDetails?.relatedContacts?.primaryContact
                    )
                  }
                >
                  <div
                    className={cx("related-contacts-item-header", {
                      secondary:
                        jobDetails?.relatedContacts?.secondaryContact !== null,
                    })}
                  >
                    <h5 className={cx("related-contacts-item-title")}>
                      {jobDetails?.relatedContacts?.primaryContact?.name}
                    </h5>

                    <div className={cx("related-contacts-item-type", "primary")}>
                      <div
                        className={cx(
                          "related-contacts-item-type-divider",
                          "primary"
                        )}
                      ></div>

                      <p
                        className={cx(
                          "related-contacts-item-type-text",
                          "primary"
                        )}
                      >
                        Primary
                      </p>
                    </div>
                  </div>

                  <div className={cx("related-contacts-item-content-container")}>
                    <div className={cx("related-contacts-item-row")}>
                      <MailIcon className={cx("icon")} />
                      <p className={cx("related-contacts-item-content")}>
                        {jobDetails?.relatedContacts?.primaryContact?.email}
                      </p>
                    </div>
                    <div className={cx("related-contacts-item-row")}>
                      <PhoneIcon className={cx("icon")} />
                      <p className={cx("related-contacts-item-content")}>
                        {jobDetails?.relatedContacts?.primaryContact?.phoneNumber}
                      </p>
                    </div>
                  </div>
                </div>

                {jobDetails?.relatedContacts?.secondaryContact !== null ? (
                  <div
                    className={cx("related-contacts-item")}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      handleOpenRelatedContact(
                        jobDetails?.relatedContacts?.secondaryContact
                      )
                    }
                  >
                    <div
                      className={cx("related-contacts-item-header", {
                        secondary:
                          jobDetails?.relatedContacts?.secondaryContact !== null,
                      })}
                    >
                      <h5 className={cx("related-contacts-item-title")}>
                        {jobDetails?.relatedContacts?.secondaryContact?.name}
                      </h5>

                      <div
                        className={cx("related-contacts-item-type", "secondary")}
                      >
                        <div className={cx("divider", "secondary")}></div>

                        <p
                          className={cx(
                            "related-contacts-item-type-text",
                            "secondary"
                          )}
                        >
                          Secondary
                        </p>
                      </div>
                    </div>

                    <div
                      className={cx("related-contacts-item-content-container")}
                    >
                      <div className={cx("related-contacts-item-row")}>
                        <MailIcon className={cx("icon")} />
                        <p className={cx("related-contacts-item-content")}>
                          {jobDetails?.relatedContacts?.secondaryContact?.email}
                        </p>
                      </div>
                      <div className={cx("related-contacts-item-row")}>
                        <PhoneIcon className={cx("icon")} />
                        <p className={cx("related-contacts-item-content")}>
                          {
                            jobDetails?.relatedContacts?.secondaryContact
                              ?.phoneNumber
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    className={cx("add-contact-button")}
                    onClick={handleAddContact}
                    type="button"
                  >
                    <PlusIcon className={cx("add-icon")} />
                    <p className={cx("add-text")}>
                      Add
                      <br />
                      Contact
                    </p>
                  </button>
                )}
              </div>
            </div>
            {jobDetails?.needAppointment === false && (
              <div className={cx("appointment")}>
                <img
                  src={calendarImage}
                  alt="appointment"
                  className={cx("appointment-image")}
                />

                <div className={cx("appointment-content")}>
                  <h3 className={cx("appointment-content-title")}>
                    Need Appointment?
                  </h3>
                  <div className={cx("appointment-content-actions")}>
                    <PrimaryButton
                      title="Completed"
                      onClick={handleOpenCompletedAppointmentModal}
                      className={cx("completed-button")}
                    />

                    <PrimaryButton
                      title="Schedule"
                      onClick={() => setOpenScheduleAppointment(true)}
                      className={cx("schedule-button")}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={cx("pictures")}>
              <div className={cx("picture-header")}>
                <h5 className={cx("picture-header-title")}>Pictures</h5>

                <OutlinedButton
                  title="View more"
                  onClick={() => {
                    handleTabChange("photos");
                  }}
                  className={cx("view-more-button")}
                />
              </div>

              <div className={cx("picture-list")}>
                {jobDetails?.photos?.map((picture) => (
                  <div className={cx("picture-item")} key={picture.id} onClick={() => {
                    setPreviewImage(picture.file?.url ?? "");
                    setOpendModalPreview(true);
                  }}>
                    <img src={picture.file?.url} alt={picture.file?.fieldName} />
                  </div>
                ))}
                <div
                  className={cx("add-item")}
                  onClick={() => {
                    setOpendUpload(true);
                  }}
                >
                  <PlusIcon className={cx("add-icon")} />
                </div>
              </div>
            </div>

            <div className={cx("documents")}>
              <div className={cx("document-header")}>
                <div className={cx("document-header-left")}>
                  <button
                    className={cx("document-header-button", {
                      active: activeTab2 === "note",
                    })}
                    onClick={() => handleTabChange2("note")}
                  >
                    Notes
                  </button>
                  <button
                    className={cx("document-header-button", {
                      active: activeTab2 === "message",
                    })}
                    onClick={() => handleTabChange2("message")}
                  >
                    Messages
                  </button>
                </div>
              </div>
              <div
                className={cx("document-content", {
                  "fade-in": !tabTransition2,
                  "fade-out": tabTransition2,
                })}
              >
                {renderTabContent2()}
              </div>
            </div>

            <UploadPhoto
              isOpen={isOpendUpload}
              onCancel={() => {
                setOpendUpload(false);
              }}
              onSave={(isCreate: boolean, data: any) => {
                handleUpload(data);
              }}
              photo={null}
            />
            </div>
          )}
        </div>
      )}

      {isOpendAddNoteModal && (
        <div
          className={cx("add-note-overlay")}
          onClick={() => setIsOpendAddNoteModal(false)}
        >
          <div
            className={cx("add-note-modal")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cx("add-note-modal-header")}>
              <h2 className={cx("add-note-modal-title")}>Add New Note</h2>
              <OutlinedButton
                title="Cancel"
                onClick={() => setIsOpendAddNoteModal(false)}
                className={cx("cancel-button")}
              />
            </div>

            <div className={cx("add-note-modal-content")}>
              <PrimaryInput
                name="title"
                label="Note Title"
                placeholder="Enter note title"
                value={dataAddNote.title}
                onChange={(e) => {
                  setDataAddNote({ ...dataAddNote, title: e.target.value });
                }}
                className={cx("input-add-note", { error: errorsAddNote.title })}
                errorMessage={errorsAddNote.title}
              />

              <div className={cx("item")}>
                <h4 className={cx("item-title")}>Note Content</h4>
                <div className={cx("mention-wrapper")} ref={mentionDropdownRef}>
                  <div
                    ref={noteContentRef}
                    className={cx("textarea", "mention-editable", "h-[100px]", {
                      error: errorsAddNote.content,
                    })}
                    contentEditable
                    data-placeholder="Enter notes. Type @ to mention employees"
                    role="textbox"
                    aria-multiline="true"
                    onInput={handleNoteContentInput}
                    onKeyDown={handleNoteContentKeyDown}
                    onPaste={handleNoteContentPaste}
                  />
                  <div
                    ref={mentionMeasureRef}
                    className={cx("mention-measure")}
                    aria-hidden="true"
                  />
                  {showMentionDropdown && mentionPosition && (
                    <div
                      ref={mentionListRef}
                      className={cx("mention-dropdown")}
                      style={{
                        position: "fixed",
                        top: `${mentionPosition.top}px`,
                        left: `${mentionPosition.left}px`,
                      }}
                    >
                      {isLoadingEmployees ? (
                        <div className={cx("mention-dropdown-empty")}>
                          Loading...
                        </div>
                      ) : filteredEmployees.length === 0 ? (
                        <div className={cx("mention-dropdown-empty")}>
                          No employees found
                        </div>
                      ) : (
                        filteredEmployees.map((emp, idx) => (
                          <button
                            key={emp.id}
                            type="button"
                            className={cx("mention-dropdown-item", {
                              active: idx === mentionDropdownIndex,
                            })}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleMentionSelect(emp);
                            }}
                          >
                            <span className={cx("mention-dropdown-name")}>
                              {getEmployeeDisplayName(emp)}
                            </span>
                            {emp.email && (
                              <span className={cx("mention-dropdown-email")}>
                                {emp.email}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {errorsAddNote.content && (
                  <p className={cx("error-text")}>{errorsAddNote.content}</p>
                )}
              </div>

              <div className={cx("checkbox-container")}>
                <input
                  type="checkbox"
                  checked={dataAddNote.private}
                  onChange={(e) =>
                    setDataAddNote({
                      ...dataAddNote,
                      private: e.target.checked,
                    })
                  }
                  className={cx("checkbox")}
                />
                <div
                  className={cx("checkbox-label")}
                  onClick={() =>
                    setDataAddNote({
                      ...dataAddNote,
                      private: !dataAddNote.private,
                    })
                  }
                >
                  Make this note private
                </div>
              </div>
            </div>

            <div className={cx("add-note-modal-footer")}>
              <PrimaryButton
                title="Add Note"
                icon={<PlusIcon className={cx("add-note-icon")} />}
                onClick={handleAddNoteSubmit}
                className={cx("add-note-button")}
              />

              <PrimaryButton
                title="Cancel"
                onClick={() => {
                  setIsOpendAddNoteModal(false);
                }}
                className={cx("cancel-button")}
              />
            </div>
          </div>
        </div>
      )}

      {isOpendAddContactModal && (
        <EditCustomerModal
          isOpen={isOpendAddContactModal}
          onClose={() => setIsOpendAddContactModal(false)}
          onSave={(data) => {
            dispatch(handleJobDetailsAddContact({ id: jobId, data }))
              .unwrap()
              .then(() => {
                notify("success", "Contact added successfully!");
                dispatch(handleGetJobDetails(jobId));
                setIsOpendAddContactModal(false);
              })
              .catch((error: any) => {
                notify(
                  "error",
                  `Failed to add contact: ${error?.response?.data?.message || "Unknown error"
                  }`
                );
              });
          }}
          mode="add"
        />
      )}

      {isOpenEditContactModal && (
        <EditCustomerModal
          isOpen={isOpenEditContactModal}
          customerDetail={editContactDetail as ICustomer}
          onClose={() => {
            setIsOpenEditContactModal(false);
            setEditContactDetail(null);
          }}
          onSave={handleSaveEditContact}
          mode="edit"
        />
      )}

      {isCompletedAppointmentModal && (
        <BaseModal
          open={isCompletedAppointmentModal}
          onClose={handleCloseCompletedAppointmentModal}
          width="90%"
          maxWidth="430px"
        >
          <div className={cx("completed-appointment-modal")}>
            <button
              className={cx("close-button")}
              onClick={handleCloseCompletedAppointmentModal}
            >
              <CloseV2Icon className={cx("close-icon")} />
            </button>

            <div className={cx("completed-appointment-modal-content")}>
              <WarningIcon className={cx("warning-icon")} />
              <h3 className={cx("completed-appointment-modal-content-title")}>
                Are you sure you want to mark
                <br />
                this appointment as
                <br />
                completed?
              </h3>

              <div
                className={cx("completed-appointment-modal-content-actions")}
              >
                <PrimaryButton
                  title="Cancel"
                  onClick={handleCloseCompletedAppointmentModal}
                  className={cx("cancel-button")}
                />

                <PrimaryButton
                  title="Yes, mark as completed"
                  onClick={handleMarkAppointmentAsCompleted}
                  className={cx("mark-as-completed-button")}
                />
              </div>
            </div>
          </div>
        </BaseModal>
      )}

      <AddAppointmentModal
        open={openScheduleAppointment}
        mode="add"
        defaultRelatedJobId={jobId}
        onClose={() => {
          setOpenScheduleAppointment(false);
          dispatch(scheduleActions.changeNeedRefreshData(true));
        }}
      />

      {isOpendModalPreview &&
        <div className={cx("overlay-preview")} onClick={() => {
          setPreviewImage('');
          setOpendModalPreview(false);
        }}>
          <div
            className={cx("upload-file-modal-container")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cx("header")}>
              <div></div>
              <CloseIcon color="#ffffff" fill="#ffffff" onClick={() => {
                setPreviewImage('');
                setOpendModalPreview(false);
              }} />
            </div>

            <div className={cx("content")}>
              <img src={previewImage} />
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default JobDetail;
